import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {
  MAX_SELECTABLE_FEATURES,
  calculateTrendLine,
  getSumMeanLegendItems,
  getTrendLineAggregation,
  handleDownloadPng,
} from 'app/components/Dashboard/utils/chartUtils';
import {
  formatMultiFeatureTableRows,
  handleDownloadCsv,
} from 'app/components/Dashboard/utils/tableUtils';
import {usePushNotification} from 'app/components/Notification';
import {ApiOrganization} from 'app/modules/Remote/Organization';

import {DashboardWidget} from '../DashboardWidget';
import {useMultiFeatureAnalysisData} from '../hooks/useMultiFeatureAnalysisData';
import {MFAResponse, MultifeatureAnalysisConfig} from '../types';
import {ChartDescription} from './ChartDescription';
import MultiFeatureAnalysisLineChart from './MultiFeatureAnalysisChart';
import {MultiFeatureAnalysisSkeleton} from './MultiFeatureAnalysisSkeleton';
import MultiFeatureAnalysisTable, {FeatureStatsRowData} from './MultiFeatureAnalysisTable';
import {MFAWidgetControls} from './WidgetControls';

interface MultiFeatureAnalysisWidgetProps {
  widgetConfig: MultifeatureAnalysisConfig;
  organization: I.ImmutableOf<ApiOrganization>;
}

export const MultiFeatureAnalysisWidget: React.FC<MultiFeatureAnalysisWidgetProps> = ({
  widgetConfig,
  organization,
}) => {
  const {layerKey} = widgetConfig;
  const chartRef = React.useRef<HTMLDivElement>(null);
  const pushNotification = usePushNotification();

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [hoveredFeatureId, setHoveredFeatureId] = React.useState<string | null>(null);
  const [displayFeatureIds, setDisplayFeatureIds] = React.useState<Set<string>>(new Set());
  const [showAggregateTrend, setShowAggregateTrend] = React.useState(true);

  const trendLineAggregation = getTrendLineAggregation(widgetConfig);

  const {data, isLoading, error, layer, allDates, noDataFound} = useMultiFeatureAnalysisData(
    organization.get('id'),
    widgetConfig
  );

  const tableRows = React.useMemo(() => {
    if (!data) return [];
    return formatMultiFeatureTableRows(data as MFAResponse);
  }, [data]);

  const trendLine = React.useMemo(() => {
    if (!data) return {};
    return calculateTrendLine(data as MFAResponse, trendLineAggregation);
  }, [data, trendLineAggregation]);

  const handleExportPng = React.useCallback(() => {
    if (!data) return;

    handleDownloadPng(
      chartRef.current?.querySelector('canvas'),
      widgetConfig,
      layer,
      isExpanded,
      () => {
        pushNotification({
          message: 'Error downloading chart',
          options: {
            intent: B.Intent.DANGER,
          },
        });
      },
      getSumMeanLegendItems({
        data,
        displayFeatureIds,
        layerKey,
        statType: widgetConfig.statAggregation,
      })
    );
  }, [data, widgetConfig, layer, isExpanded, displayFeatureIds, layerKey, pushNotification]);

  const exportTableAsCsv = React.useCallback(() => {
    const exportRows = [...tableRows];
    exportRows.unshift({
      id: 'trendline',
      name: trendLineAggregation === 'sum' ? 'Total' : 'Average',
      ...trendLine,
    } as FeatureStatsRowData);

    handleDownloadCsv(exportRows, widgetConfig, () => {
      pushNotification({
        message: 'Error exporting table as CSV',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    });
  }, [tableRows, widgetConfig, trendLineAggregation, trendLine, pushNotification]);

  const setDisplayFeatureIdsWithLimit: React.Dispatch<React.SetStateAction<Set<string>>> = (
    input: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => {
    const sendLimitNotification = () =>
      pushNotification({
        message: `A maximum of ${MAX_SELECTABLE_FEATURES} properties can be displayed at once`,
        options: {
          intent: B.Intent.WARNING,
        },
      });
    if (typeof input === 'function') {
      setDisplayFeatureIds((prev) => {
        const newFeatureIds = input(prev);
        if (newFeatureIds.size >= MAX_SELECTABLE_FEATURES) {
          sendLimitNotification();
          return prev;
        }
        return newFeatureIds;
      });
    } else {
      if (input.size >= MAX_SELECTABLE_FEATURES) {
        sendLimitNotification();
        return;
      } else {
        setDisplayFeatureIds(input);
      }
    }
  };

  if (isLoading) {
    return <MultiFeatureAnalysisSkeleton />;
  }

  return (
    <DashboardWidget
      title={widgetConfig.title}
      size={isExpanded ? 'large' : 'small'}
      controls={
        <MFAWidgetControls
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          onExportPng={handleExportPng}
          onExportCsv={exportTableAsCsv}
        />
      }
    >
      {error || !data || noDataFound ? (
        <B.NonIdealState
          icon="error"
          iconMuted={false}
          title="No data"
          description={
            noDataFound
              ? `No property statistics found for ${layer.display}`
              : (error?.message ?? 'Error visualizing data')
          }
        />
      ) : (
        <>
          <ChartDescription
            widgetConfig={widgetConfig}
            layer={layer}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
          />

          <div ref={chartRef}>
            <MultiFeatureAnalysisLineChart
              data={data as MFAResponse}
              displayFeatureIds={displayFeatureIds}
              hoveredFeatureId={hoveredFeatureId}
              isExpanded={isExpanded}
              setDisplayFeatureIds={setDisplayFeatureIdsWithLimit}
              widgetConfig={widgetConfig}
              showAggregateTrend={showAggregateTrend}
              trendLineAggregation={trendLineAggregation}
            />
          </div>

          {isExpanded && (
            <MultiFeatureAnalysisTable
              displayFeatureIds={displayFeatureIds}
              setDisplayFeatureIds={setDisplayFeatureIdsWithLimit}
              rows={tableRows}
              allDates={allDates}
              setShowAggregateTrend={setShowAggregateTrend}
              showAggregateTrend={showAggregateTrend}
              widgetConfig={widgetConfig}
              setHoveredFeatureId={setHoveredFeatureId}
              trendLine={trendLine}
              trendLineAggregation={trendLineAggregation}
            />
          )}
        </>
      )}
    </DashboardWidget>
  );
};
